import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
    Table,
    Button,
    Flex,
    Breadcrumb,
    Form,
    message,
    Popconfirm,
    Space,
    Pagination,
    Switch,
    TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import SizeModel from './SizeModel'; // This is the modal component
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
    customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Size {
    size_id: number;
    size_name: string;
    size_status: number;
}

const SizeMaster: React.FC = () => {
    const [sizes, setSizes] = useState<Size[]>([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState<Size | null>(null);
    const { styles } = useStyle();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const currentModule = useCurrentModule();

    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});

    const cancel: PopconfirmProps['onCancel'] = (e) => {
        console.log(e);
        showError('Click on No');
    };

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const fetchSizes = async () => {
        try {
            const data = await apiRequest<Size[]>({
                method: 'get',
                url: 'sizes',
            });
            setSizes(data);
        } catch (error) {
            showError('Failed to fetch sizes');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchSizes();
    }, []);

    const handleEditClick = (record: Size) => {
        setIsEditMode(true);
        setInitialValues({
            size_id: record.size_id,
            size_name: record.size_name,
            size_status: record.size_status,
        });
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        fetchSizes();
    };

    const showCreateModal = () => {
        setIsEditMode(false);
        setInitialValues(null);
        setIsModalVisible(true);
    };

    const hideCreateModal = () => {
        setIsModalVisible(false);
        setIsEditMode(false);
        setInitialValues(null);
    };

    const handleDelete = async (size_id: number) => {
        try {
            await apiRequest({
                method: 'delete',
                url: `/delete-size/${size_id}`,
            });
            showSuccess('Size deleted');
            fetchSizes();
        } catch {
            showError('Failed to delete size');
        }
    };

    const handleStatusToggle = async (size_id: number) => {
        setSizes((prev) =>
            prev.map((item) =>
                item.size_id === size_id
                    ? { ...item, size_status: item.size_status === 1 ? 0 : 1 }
                    : item
            )
        );

        try {
            await apiRequest({
                method: 'put',
                url: `/update-status-size/${size_id}`,
            });
            showSuccess('Size status updated');
        } catch (error) {
            showError('Failed to update status');
            setSizes((prev) =>
                prev.map((item) =>
                    item.size_id === size_id
                        ? { ...item, size_status: item.size_status === 1 ? 0 : 1 }
                        : item
                )
            );
        }
    };

    const handleTableChange: TableProps<Size>['onChange'] = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    const columns: TableColumnsType<Size> = [
        {
            title: 'Size',
            dataIndex: 'size_name',
            key: 'size_name',
            filters: Array.from(new Set(sizes.map((s) => s.size_name))).map((name) => ({
                text: name,
                value: name,
            })),
            filterSearch: true,
            filteredValue: filteredInfo.size_name || null,
            onFilter: (value, record) => record.size_name.includes(value as string),
            sorter: (a, b) => a.size_name.localeCompare(b.size_name),
            sortOrder: sortedInfo.columnKey === 'size_name' ? sortedInfo.order : null,
            ellipsis: true,
            render: (_, record) => <div>{record.size_name}</div>,
        },
    ];

    if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
        columns.push({
            title: 'Action',
            width: 200,
            render: (_: any, record: Size) => (
                <Space size="middle">
                    {currentModule?.right_edit === 'Y' && (
                        <>
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                                checked={!!record.size_status}
                                onChange={() => handleStatusToggle(record.size_id)}
                            />
                            <Button onClick={() => handleEditClick(record)} className="px-8">
                                <EditOutlined />
                            </Button>
                        </>
                    )}
                    {currentModule?.right_delete === 'Y' && (
                        <Popconfirm
                            title="Delete this size?"
                            onConfirm={() => handleDelete(record.size_id)}
                            onCancel={cancel}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger className="px-8">
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        });
    }

    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: 16,
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
                    <Breadcrumb.Item href="/">
                        <HomeOutlined />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>View Sizes</Breadcrumb.Item>
                </Breadcrumb>

                {currentModule?.right_add === 'Y' && (
                    <Flex gap="small">
                        <Button type="primary" onClick={showCreateModal}>
                            Add Size
                        </Button>
                    </Flex>
                )}
            </Space>

            <div
                style={{
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Table<Size>
                    tableLayout="auto"
                    className={`${styles.customTable} scrollable-table`}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="size_id"
                    dataSource={sizes.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    bordered
                    pagination={false}
                    scroll={{ y: 37 * 11 }}
                    locale={{ emptyText: 'No size found' }}
                    footer={() => (
                        <Flex justify="space-between">
                            <span>
                                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{Math.min(pagination.current * pagination.pageSize, sizes.length)} of {sizes.length}
                            </span>
                            <Pagination
                                {...pagination}
                                total={sizes.length}
                                showSizeChanger
                                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
                            />
                        </Flex>
                    )}
                />
            </div>

            <SizeModel
                visible={isModalVisible}
                onCancel={hideCreateModal}
                onCreate={handleCreate}
                isEditMode={isEditMode}
                initialValues={initialValues}
            />
        </>
    );
};

export default SizeMaster;
